import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, useTheme, styled, keyframes } from '@mui/material';
import { Link as RouterLink, useLocation, LinkProps } from 'react-router-dom';
import { Home, Upload, Description, BatteryChargingFull, Feedback, PhotoLibrary } from '@mui/icons-material';
import ThemeToggle from './ThemeToggle';

// Gradient animation for logo
const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const GradientAppBar = styled(AppBar)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.background.paper}80 30%, ${theme.palette.primary.main}15 70%, ${theme.palette.primary.main}10 100%)`,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderBottom: `1px solid ${theme.palette.primary.main}20`,
  boxShadow: `0 4px 30px ${theme.palette.primary.main}10`,
}));

// Helper function for logo styles
const getLogoStyles = (theme: any) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 4s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 800,
  fontSize: '1.5rem',
  letterSpacing: '-0.02em',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  textDecoration: 'none',
  '&:hover': {
    opacity: 0.8,
    transition: 'opacity 0.3s ease'
  }
});

// Helper function to get button styles
const getNavButtonStyles = (theme: any, isActive: boolean) => ({
  padding: '10px 20px',
  borderRadius: '12px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  background: isActive
    ? `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.main}15 50%, ${theme.palette.secondary.main}10 100%)`
    : 'transparent',
  border: isActive
    ? `1px solid ${theme.palette.primary.main}40`
    : '1px solid transparent',
  color: isActive ? theme.palette.primary.main : theme.palette.text.primary,

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}20, transparent)`,
    transition: 'left 0.5s',
  },

  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}25 0%, ${theme.palette.primary.main}20 50%, ${theme.palette.secondary.main}15 100%)`,
    border: `1px solid ${theme.palette.primary.main}50`,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 20px ${theme.palette.primary.main}20`,

    '&::before': {
      left: '100%',
    }
  },
});

const Header: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();

  return (
    <GradientAppBar position="sticky" elevation={0}>
      <Toolbar sx={{ py: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={getLogoStyles(theme)}
          >
            <BatteryChargingFull sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            Smart Storage Checker
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<Home />}
            sx={getNavButtonStyles(theme, location.pathname === '/')}
          >
            Start
          </Button>

          <Button
            component={RouterLink}
            to="/vorlagen"
            startIcon={<Description />}
            sx={getNavButtonStyles(theme, location.pathname === '/vorlagen')}
          >
            Vorlagen
          </Button>

          <Button
            component={RouterLink}
            to="/upload"
            startIcon={<Upload />}
            sx={getNavButtonStyles(theme, location.pathname === '/upload')}
          >
            Upload
          </Button>

          <Button
            component={RouterLink}
            to="/galerie"
            startIcon={<PhotoLibrary />}
            sx={getNavButtonStyles(theme, location.pathname === '/galerie')}
          >
            Galerie
          </Button>

          <Button
            component={RouterLink}
            to="/feedback"
            startIcon={<Feedback />}
            sx={getNavButtonStyles(theme, location.pathname === '/feedback')}
          >
            Feedback
          </Button>

          <Box sx={{ ml: 1 }}>
            <ThemeToggle />
          </Box>
        </Box>
      </Toolbar>
    </GradientAppBar>
  );
};

export default Header;